/**
 * ``useDetectionDetail`` — TanStack Query hook around
 * ``GET /api/query/detection/{report_id}/details`` (PR4; REQ-QUERY-024).
 *
 * Feeds the Entity Details panel's **Detection** sections (IDENTITY /
 * CLASSIFICATION / TRACK-OBJECT / GEOMETRY / SIGNAL / BEHAVIOUR /
 * ASSOCIATIONS / DETECTED BY / PROVENANCE — REQ-UI-066) when a detection
 * is selected in the bus. The selection bus carries only the
 * ``report_id``; the hook fetches the rest (the full classification tree,
 * behaviours, RF signal, track / object key-values, velocity, colour, and
 * cross-sensor associations the list shape :class:`DetectionItem` doesn't
 * project).
 *
 * Behaviour: a read-role gate, ``404`` → ``null`` (the detection aged out of
 * the queryable window, an empty state the sections render, **not** an
 * error), and a no-retry-on-{401,403,404} predicate. The Alert detail hook
 * (:func:`entity-details/use-alert-detail`) shares this exact shape.
 *
 * Cross-references:
 *   - REQ-QUERY-024 (Detection detail surface) / REQ-AUTH-024.
 *   - REQ-UI-066 (Detection section schema).
 *   - ADR-032 §4 (Detection data model — the nested-field source of truth).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type DetectionDetail = components["schemas"]["DetectionDetail"];

/** Same read-role gate as the other Entity Details endpoints (REQ-AUTH-024..027). */
export const DETECTION_DETAIL_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/** Detections are immutable once recorded; a longer stale time than the
 * status sections is fine (no live refetch needed). Mirrors the Event
 * detail hook. */
export const DETECTION_DETAIL_STALE_TIME_MS = 60_000;

export function detectionDetailQueryKey(reportId: string): readonly unknown[] {
  return ["detection-detail", reportId] as const;
}

export interface UseDetectionDetailOptions {
  reportId: string | null;
}

export function useDetectionDetail(
  options: UseDetectionDetailOptions,
): UseQueryResult<DetectionDetail | null, ApiError> {
  const session = useSession();
  const hasRole = session.hasAnyRole(DETECTION_DETAIL_READER_ROLES);
  const enabled = options.reportId !== null && hasRole;

  return useQuery<DetectionDetail | null, ApiError>({
    queryKey:
      options.reportId !== null
        ? detectionDetailQueryKey(options.reportId)
        : ["detection-detail", "disabled"],
    queryFn: async ({ signal }) => {
      if (options.reportId === null) {
        throw new ApiError(0, null, "useDetectionDetail: query ran without reportId");
      }
      const url = `/api/query/detection/${encodeURIComponent(options.reportId)}/details`;
      try {
        const raw = await apiFetch<unknown>(url, { signal });
        if (!isDetectionDetail(raw)) {
          console.error(
            "[meridian/entity-details/use-detection-detail] response failed shape validation",
            raw,
          );
          throw new ApiError(
            500,
            raw,
            "detection-detail response did not match the expected shape",
          );
        }
        return raw;
      } catch (err) {
        // 404 = no detection with that report_id → empty state, not error.
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled,
    staleTime: DETECTION_DETAIL_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Minimal structural guard for a :type:`DetectionDetail`. Validates the
 * always-present typed columns (the nested ``classification[]`` /
 * ``signal[]`` / … arrays are optional, so they're not part of the guard);
 * a malformed payload that slips this guard surfaces as a 500 the sections
 * render as an error rather than rendering ``undefined`` fields.
 */
function isDetectionDetail(value: unknown): value is DetectionDetail {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.report_id === "string" &&
    typeof obj.object_id === "string" &&
    typeof obj.producer_id === "string" &&
    typeof obj.time === "string" &&
    typeof obj.message_hash === "string" &&
    typeof obj.schema_version === "number"
  );
}
